import React, { useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import StepHeading from './StepHeading';

interface Step5Props {
  descriptions: string[];
  editedDescriptions: string[];
  setEditedDescriptions: (descriptions: string[]) => void;
  selectedDescription: string;
  setSelectedDescription: (description: string) => void;
  selectedDescriptionIndex: number;
  setSelectedDescriptionIndex: (index: number) => void;
  handleStep5: () => void;
  loading: boolean;
  isGeneratingDescriptions: boolean;
}

const DESCRIPTION_LABELS = ['Modern & Aspirational', 'Playful & Purposeful'];

const RichEditor: React.FC<{
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onCommit: () => void;
}> = ({ value, placeholder, onChange, onFocus, onCommit }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div
      ref={ref}
      className="ed-doc-body"
      role="textbox"
      aria-multiline="true"
      aria-label={placeholder}
      contentEditable
      spellCheck={false}
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onInput={() => ref.current && onChange(ref.current.innerHTML)}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onCommit();
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
      }}
    />
  );
};

const Step5: React.FC<Step5Props> = ({
  descriptions,
  editedDescriptions,
  setEditedDescriptions,
  selectedDescriptionIndex,
  setSelectedDescription,
  setSelectedDescriptionIndex,
  handleStep5,
  loading,
  isGeneratingDescriptions,
}) => {
  const select = (index: number) => {
    setSelectedDescriptionIndex(index);
    setSelectedDescription(editedDescriptions[index] || descriptions[index]);
  };

  const edit = (index: number, value: string) => {
    const next = [...editedDescriptions];
    next[index] = value;
    setEditedDescriptions(next);
    if (selectedDescriptionIndex === index) setSelectedDescription(value);
  };

  return (
    <div className="ed-enter">
      <StepHeading
        index={5}
        kicker="Copy"
        title="Two ways to tell the same story."
        lede="Choose the voice that sounds like your product, then rewrite any line directly on the page."
      />

      <div className="ed-block">
        <div className="ed-docs" role="radiogroup" aria-label="Product descriptions">
          {[0, 1].map((index) => {
            if (isGeneratingDescriptions || !descriptions[index]) {
              return (
                <div key={index} className="ed-skeleton ed-skeleton--tall">
                  Writing {DESCRIPTION_LABELS[index]}
                </div>
              );
            }

            const isSelected = selectedDescriptionIndex === index;

            return (
              <div
                key={index}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                className={`ed-doc${isSelected ? ' is-selected' : ''}`}
                onClick={() => select(index)}
                onKeyDown={(e) => {
                  if ((e.target as HTMLElement).isContentEditable) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    select(index);
                  }
                }}
              >
                {isSelected && (
                  <span className="ed-mark" aria-hidden="true">
                    <Check />
                  </span>
                )}

                <div className="ed-doc-head">
                  <span className="ed-doc-label">
                    {String(index + 1).padStart(2, '0')} — {DESCRIPTION_LABELS[index]}
                  </span>
                </div>

                <RichEditor
                  value={editedDescriptions[index] || descriptions[index]}
                  placeholder={`${DESCRIPTION_LABELS[index]} copy`}
                  onChange={(value) => edit(index, value)}
                  onFocus={() => select(index)}
                  onCommit={() => {
                    if (!loading) handleStep5();
                  }}
                />
              </div>
            );
          })}
        </div>
        <p className="ed-hint">Edits save as you type. Press ⌘ + Enter to continue.</p>
      </div>
    </div>
  );
};

export default Step5;
